import React, { useMemo, useState, Component } from 'react';
import { ArrowLeft, Check, Share2, Download } from 'lucide-react';
import { Store } from '../../types';
import { MOCK_CAFE_LISTS, STORES } from '../../data/mockData';
import { PolaroidCard } from '../PolaroidCard';
import { useToast } from '../ToastContext';
interface MixAndShareScreenProps {
  onBack: () => void;
}
const MAX_SELECTION = 6;
const MIN_SELECTION = 1;
// Aesthetic, deterministic rotation pattern
const STACK_ROTATIONS = [-8, 5, -4, 9, -6, 3];
export function MixAndShareScreen({
  onBack
}: MixAndShareScreenProps) {
  const {
    showToast
  } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [caption, setCaption] = useState('My Bangalore picks ✨');
  // Pool: union of all cafe lists, deduped
  const availableStores = useMemo<Store[]>(() => {
    const ids = new Set<string>();
    MOCK_CAFE_LISTS.forEach(list => list.cafes.forEach(c => ids.add(c.storeId)));
    return Array.from(ids).map(id => STORES.find(s => s.id === id)).filter((s): s is Store => Boolean(s));
  }, []);
  const toggle = (id: string) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_SELECTION) {
        showToast(`Pick up to ${MAX_SELECTION} places`, 'info');
        return prev;
      }
      return [...prev, id];
    });
  };
  const selectedStores = selected.map(id => STORES.find(s => s.id === id)).filter((s): s is Store => Boolean(s));
  const canShare = selected.length >= MIN_SELECTION;
  const handleShareWhatsApp = () => {
    if (!canShare) return;
    const names = selectedStores.map(s => s.name).join(', ');
    const message = `${caption}\n\n${names}\n\nShared via Kraya ☕`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  const handleSave = () => {
    if (!canShare) return;
    showToast('Polaroid stack saved!', 'success');
  };
  return <div className="flex flex-col h-full bg-white pb-32" data-id="element-1354">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100" data-id="element-1355">
        <div className="flex items-center gap-3 px-4 py-4" data-id="element-1356">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1357">
            <ArrowLeft size={22} data-id="element-1358" />
          </button>
          <div className="flex-1" data-id="element-1359">
            <h1 className="text-lg font-bold text-gray-900" data-id="element-1360">Mix & Share</h1>
            <p className="text-xs text-gray-500" data-id="element-1361">
              Pick 1–{MAX_SELECTION} cafes · {selected.length} selected
            </p>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="bg-gradient-to-br from-yellow-50 via-white to-purple-50 border-b border-gray-100 px-4 py-5" data-id="element-1362">
        <div className="bg-gray-50 rounded-2xl p-4 min-h-[200px] flex flex-col items-center justify-center" data-id="element-1363">
          {selectedStores.length === 0 ? <p className="text-sm text-gray-400 italic" data-id="element-1364">
              Select cafes below to build your stack
            </p> : <>
              <div className="relative w-full h-44 flex items-center justify-center" data-id="element-1365">
                {selectedStores.map((store, i) => {
              const total = selectedStores.length;
              const center = (total - 1) / 2;
              const offsetX = (i - center) * 38;
              return <div key={store.id} className="absolute" style={{
                left: '50%',
                transform: `translateX(${offsetX - 60}px)`,
                zIndex: i
              }} data-id="element-1366">
                      <PolaroidCard store={store} rotation={STACK_ROTATIONS[i % STACK_ROTATIONS.length]} size="sm" showTape={i === 0} data-id="element-1367" />
                    </div>;
            })}
              </div>
              <input type="text" value={caption} onChange={e => setCaption(e.target.value)} maxLength={40} className="mt-3 w-full bg-transparent text-center text-base font-bold text-gray-800 focus:outline-none border-b border-dashed border-gray-300 pb-1" style={{
            fontFamily: "'Caveat', cursive"
          }} data-id="element-1368" />
            </>}
        </div>
      </div>

      {/* Selection grid */}
      <div className="px-4 py-4 flex-1" data-id="element-1369">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3" data-id="element-1370">
          Your cafes
        </h2>
        <div className="grid grid-cols-3 gap-2" data-id="element-1371">
          {availableStores.map(store => {
          const isSelected = selected.includes(store.id);
          const order = selected.indexOf(store.id) + 1;
          return <button key={store.id} onClick={() => toggle(store.id)} className={`relative rounded-xl overflow-hidden aspect-square transition-all active:scale-95 ${isSelected ? 'ring-4 ring-[#FFFC00] ring-offset-1' : 'ring-1 ring-gray-200'}`} data-id="element-1372">
                <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-1373" />
                <div className={`absolute inset-0 transition-colors ${isSelected ? 'bg-black/30' : 'bg-black/0'}`} data-id="element-1374" />
                {isSelected && <div className="absolute top-1 right-1 w-6 h-6 bg-[#FFFC00] rounded-full flex items-center justify-center shadow-md" data-id="element-1375">
                    <span className="text-xs font-bold text-black" data-id="element-1376">
                      {order}
                    </span>
                  </div>}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5" data-id="element-1377">
                  <p className="text-[10px] font-semibold text-white truncate" data-id="element-1378">
                    {store.name}
                  </p>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 w-full max-w-md mx-auto bg-white border-t border-gray-100 p-4 grid grid-cols-2 gap-2" data-id="element-1379">
        <button onClick={handleSave} disabled={!canShare} className="flex items-center justify-center gap-2 py-3 bg-gray-100 text-black rounded-xl font-bold text-sm active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100" data-id="element-1380">
          <Download size={16} data-id="element-1381" />
          Save
        </button>
        <button onClick={handleShareWhatsApp} disabled={!canShare} className="flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl font-bold text-sm active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100" data-id="element-1382">
          <Share2 size={16} data-id="element-1383" />
          WhatsApp
        </button>
      </div>
    </div>;
}